<template>
  <div class="whatsapp-page">
    <div class="page-header mb-4">
      <h2 class="page-title mb-1">Nova Solicitação</h2>
      <p class="page-subtitle mb-0">{{ stepSubtitle }}</p>
    </div>

      <div
        v-if="skipsProfileStep"
        class="steps-bar mb-4"
        :class="{ 'steps-bar--progress': currentStep > 2 }"
      >
        <div
          class="step-item step-item--clickable"
          :class="{
            'step-item--active': currentStep === 2,
            'step-item--done': currentStep > 2,
          }"
          @click="handleStepClick(2)"
        >
          <span v-if="currentStep > 2" class="step-number step-number--check">
            <v-icon small color="white">{{ icons.mdiCheck }}</v-icon>
          </span>
          <span v-else class="step-number">1</span>
          <span>Informações</span>
        </div>
        <div class="step-divider" :class="{ 'step-divider--done': currentStep > 2 }" />
        <div
          class="step-item step-item--clickable"
          :class="{ 'step-item--active': currentStep === 3 }"
          @click="handleStepClick(3)"
        >
          <span class="step-number">2</span>
          <span>Mensagem</span>
        </div>
      </div>

      <div
        v-else
        class="steps-bar mb-4"
        :class="{ 'steps-bar--progress': currentStep > 1 }"
      >
        <div
          class="step-item step-item--clickable"
          :class="{
            'step-item--active': currentStep === 1,
            'step-item--done': currentStep > 1,
          }"
          @click="currentStep = 1"
        >
          <span v-if="currentStep > 1" class="step-number step-number--check">
            <v-icon small color="white">{{ icons.mdiCheck }}</v-icon>
          </span>
          <span v-else class="step-number">1</span>
          <span>Perfil</span>
        </div>
        <div class="step-divider" :class="{ 'step-divider--done': currentStep > 1 }" />
        <div
          class="step-item step-item--clickable"
          :class="{
            'step-item--active': currentStep === 2,
            'step-item--done': currentStep > 2,
            'step-item--disabled': !canAccessCampaignSteps,
          }"
          @click="handleStepClick(2)"
        >
          <span v-if="currentStep > 2" class="step-number step-number--check">
            <v-icon small color="white">{{ icons.mdiCheck }}</v-icon>
          </span>
          <span v-else class="step-number">2</span>
          <span>Informações</span>
        </div>
        <div class="step-divider" :class="{ 'step-divider--done': currentStep > 2 }" />
        <div
          class="step-item step-item--clickable"
          :class="{
            'step-item--active': currentStep === 3,
            'step-item--disabled': !canAccessCampaignSteps,
          }"
          @click="handleStepClick(3)"
        >
          <span class="step-number">3</span>
          <span>Mensagem</span>
        </div>
      </div>

      <v-alert type="warning" dense outlined class="mb-4">
        <v-icon small left>mdi-alert</v-icon>
        <strong>Importante:</strong> A campanha deve ser realizada com no mínimo 3 horas de antecedência.
      </v-alert>

      <v-form autocomplete="off">
        <validation-observer ref="formWhatsapp">
          <v-row dense v-if="currentStep === 1 && !skipsProfileStep" justify="center" class="step-profile-row">
            <v-col cols="12" md="7" lg="6">
              <v-alert
                v-if="hasPendingChannelRequest"
                type="info"
                dense
                outlined
                class="mb-3"
              >
                Sua solicitação de canal está <strong>{{ channelStatusLabel }}</strong>.
                Aguarde a aprovação do administrador para continuar com a campanha.
              </v-alert>

              <v-alert
                v-if="latestRefusedChannelRequest && canSubmitChannelRequest"
                type="warning"
                dense
                outlined
                class="mb-3"
              >
                Sua última solicitação foi recusada
                <span v-if="latestRefusedChannelRequest.refusedReason">
                  — {{ latestRefusedChannelRequest.refusedReason }}
                </span>.
                Você pode enviar uma nova solicitação abaixo.
              </v-alert>

              <v-card outlined class="panel-card mb-3">
                <v-card-title class="panel-title ">Criar Canal</v-card-title>
                <v-card-subtitle class="pt-0">
                  Antes de disparar a campanha, você precisa criar um canal de WhatsApp.
                </v-card-subtitle>
                <v-card-text>
                  <template v-if="hasPendingChannelRequest">
                    <div class="text-center mb-2">
                      <v-avatar
                        v-if="pendingChannelRequest.idfileProfileImage"
                        size="72"
                        class="mb-2"
                      >
                        <img
                          :src="getPublicImageUrl(pendingChannelRequest.idfileProfileImage)"
                          alt="Foto de Perfil"
                        >
                      </v-avatar>
                      <div class="text-subtitle-1 font-weight-medium">
                        {{ pendingChannelRequest.profileName }}
                      </div>
                      <v-chip small color="info" class="mt-2">
                        {{ channelStatusLabel }}
                      </v-chip>
                    </div>
                  </template>

                  <template v-else>
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome do Perfil"
                    rules="required"
                    vid="profileName"
                  >
                    <v-text-field
                      v-model="profileName"
                      :error-messages="errors"
                      dense
                      label="Nome de Exibição *"
                      outlined
                    />
                  </validation-provider>

                  <validation-provider v-slot="{ errors }" name="Foto de Perfil" vid="profilePhoto">
                    <v-card outlined class="upload-card">
                      <v-card-text>
                        <input
                          ref="profilePhotoFile"
                          type="file"
                          accept="image/*"
                          hidden
                          @change="handleProfilePhoto"
                        />
                        <v-btn
                          color="primary"
                          outlined
                          block
                          class="dashed-border"
                          :disabled="profilePhotoLoading"
                          @click="$refs.profilePhotoFile.click()"
                        >
                          <v-icon left>{{ icons.mdiImage }}</v-icon>
                          {{ profilePhotoSelected ? 'Alterar Foto de Perfil' : 'Selecionar Foto de Perfil' }}
                        </v-btn>

                        <div v-if="profilePhotoLoading" class="mt-2 text-center">
                          <v-progress-circular indeterminate color="primary" size="20" />
                          <span class="ml-2">Processando imagem...</span>
                        </div>

                        <div v-if="profilePhotoSelected && !profilePhotoLoading" class="mt-3 text-center">
                          <v-avatar size="72">
                            <img :src="profilePhotoPreviewUrl" alt="Foto de Perfil" />
                          </v-avatar>
                          <div class="mt-2">
                            <v-chip close color="primary" outlined small @click:close="removeProfilePhoto">
                              {{ profilePhotoFileName }}
                            </v-chip>
                          </div>
                        </div>

                        <div v-if="errors.length" class="mt-2">
                          <span class="error--text">{{ errors[0] }}</span>
                        </div>
                      </v-card-text>
                    </v-card>
                      </validation-provider>
                  </template>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense v-if="currentStep === 2" class="step-info-row">
            <v-col cols="12">
              <v-card outlined class="panel-card panel-card--full-height mb-3">
                <v-card-title class="panel-title">Informações do Disparo</v-card-title>
                <v-card-subtitle class="pt-0">Preencha os dados obrigatórios para sua campanha</v-card-subtitle>
                <v-card-text>
                  <validation-provider
                    v-slot="{ errors }"
                    name="Nome da Campanha"
                    rules="required"
                    vid="campaignName"
                  >
                    <v-text-field
                      v-model="campaignName"
                      :error-messages="errors"
                      dense
                      label="Título da Campanha *"
                      outlined
                    />
                  </validation-provider>

                  <validation-provider
                    v-if="approvedChannelOptions.length > 0"
                    v-slot="{ errors }"
                    name="Marca"
                    rules="required"
                    vid="selectedChannelId"
                  >
                    <v-select
                      v-model="selectedChannelId"
                      :items="approvedChannelOptions"
                      :error-messages="errors"
                      item-text="text"
                      item-value="value"
                      dense
                      outlined
                      label="Marca *"
                      hint="Selecione o canal que será usado no disparo"
                      persistent-hint
                      class="mb-2"
                    />
                  </validation-provider>

                  <v-alert
                    v-if="hasPendingChannelRequest"
                    type="info"
                    dense
                    outlined
                    class="mb-4"
                  >
                    Você tem uma solicitação de canal <strong>{{ channelStatusLabel }}</strong>.
                    Aguarde a aprovação para utilizá-la nas campanhas.
                  </v-alert>

                  <validation-provider
                    v-slot="{ errors }"
                    name="Data de Envio"
                    rules="required"
                    vid="scheduledDate"
                  >
                    <v-datetime-picker
                      v-model="scheduledDate"
                      :text-field-props="{
                        errorMessages: errors,
                        outlined: true,
                        dense: true,
                        placeholder: 'Selecione a data e hora de envio',
                        label: 'Data e horário *',
                        hint: 'Mínimo de 3 horas de antecedência',
                        persistentHint: true,
                        min: getMinScheduledDate(),
                      }"
                      date-format="dd/MM/yyyy"
                      time-format="HH:mm"
                      hide-details
                    >
                      <template slot="dateIcon">
                        <v-icon>{{ icons.mdiCalendarMonthOutline }}</v-icon>
                      </template>
                      <template slot="timeIcon">
                        <v-icon>{{ icons.mdiClockTimeElevenOutline }}</v-icon>
                      </template>
                    </v-datetime-picker>
                  </validation-provider>

                  <v-select
                    v-model="recipientType"
                    :items="recipientTypes"
                    item-text="text"
                    item-value="value"
                    dense
                    outlined
                    label="Origem da Lista de Contatos"
                    @change="handleRecipientTypeChange"
                  />

                  <v-card v-if="recipientType === 'csv'" outlined class="upload-card">
                    <v-card-text>
                      <v-file-input
                        v-model="csvFile"
                        accept=".csv"
                        label="Lista de Contatos *"
                        outlined
                        dense
                        prepend-icon=""
                        :prepend-inner-icon="icons.mdiFileUpload"
                        @change="handleCsvFile"
                      />
                      <p class="text-caption mb-0">
                        Coluna "telefone" obrigatória. Demais colunas viram variáveis na mensagem
                        (ex.: NOME → <code v-pre>{{NOME}}</code>).
                      </p>
                    </v-card-text>
                  </v-card>

                  <v-card v-else outlined class="upload-card">
                    <v-card-text>
                      <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px;">
                        <v-text-field
                          v-model="newCustomVariable"
                          dense
                          outlined
                          hide-details
                          label="Criar variável"
                          placeholder="Ex.: NOME"
                          class="variable-create-field"
                          @keyup.enter="addCustomVariable"
                        />
                        <v-btn
                          color="primary"
                          outlined
                          dense
                          :disabled="!canAddCustomVariable"
                          @click="addCustomVariable"
                        >
                          <v-icon left small>{{ icons.mdiPlus }}</v-icon>
                          Adicionar
                        </v-btn>
                      </div>
                      <div v-if="customVariables.length" class="mb-3">
                        <v-chip
                          v-for="variable in customVariables"
                          :key="'custom-var-' + variable"
                          class="ma-1"
                          close
                          outlined
                          color="primary"
                          small
                          @click:close="removeCustomVariable(variable)"
                        >
                          {{ variable }}
                        </v-chip>
                      </div>
                      <v-textarea
                        v-model="contactsTextarea"
                        :label="pasteContactsLabel"
                        outlined
                        dense
                        rows="5"
                        :placeholder="pasteContactsPlaceholder"
                        @input="processContactsText"
                      />
                      <p v-if="customVariables.length" class="text-caption mb-2">
                        Formato: NUMERO,<span v-for="(variable, idx) in customVariables" :key="'fmt-' + variable">{{ variable }}<template v-if="idx < customVariables.length - 1">,</template></span> (um contato por linha).
                      </p>
                      <v-btn color="error" outlined small @click="resetContacts">Limpar lista</v-btn>
                    </v-card-text>
                  </v-card>

                  <div class="text-caption mt-2">
                    Contatos carregados: <strong>{{ contacts.length.toLocaleString('pt-BR') }}</strong>
                  </div>
                  <div v-if="availableVariables.length" class="mt-2">
                    <span class="text-caption">Variáveis detectadas:</span>
                    <v-chip
                      v-for="variable in availableVariables"
                      :key="'detected-' + variable"
                      class="ma-1"
                      small
                      outlined
                      color="primary"
                    >
                      {{ variable }}
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense v-if="currentStep === 3" class="step-two-row align-stretch">
            <v-col cols="12" lg="7">
              <div class="step-two-stack">
                <!-- Mídia -->
                <v-card outlined class="panel-card step-two-subcard mb-3">
                  <v-card-title class="step-two-card-title">
                    <v-icon left small class="mr-1">{{ icons.mdiImage }}</v-icon>
                    Mídia
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <v-select
                      v-model="mediaMode"
                      :items="mediaModeItems"
                      item-text="text"
                      item-value="value"
                      dense
                      outlined
                      hide-details
                      label="Tipo de mídia"
                      @change="onMediaModeChange"
                    />
                    <input
                      ref="mediaFile"
                      type="file"
                      :accept="mediaMode === 'image' ? 'image/*' : 'video/*'"
                      hidden
                      @change="handleMediaFile"
                    />
                    <div
                      class="drop-zone mt-3"
                      :class="{ 'drop-zone-active': isDragging }"
                      @dragover.prevent="handleDragOver"
                      @dragleave.prevent="handleDragLeave"
                      @drop.prevent="handleDrop"
                    >
                      <v-btn
                        color="primary"
                        outlined
                        block
                        large
                        class="dashed-border media-upload-btn"
                        :disabled="mediaLoading"
                        @click="$refs.mediaFile.click()"
                      >
                        <v-icon left>{{ icons.mdiPaperclip }}</v-icon>
                        {{ isDragging ? 'Solte o arquivo aqui' : 'Clique ou arraste o arquivo' }}
                      </v-btn>
                    </div>
                    <p v-if="mediaMode === 'video'" class="text-caption text--secondary mt-1 mb-0">
                      Tamanho máximo do vídeo: 16MB
                    </p>
                    <div v-if="mediaLoading" class="mt-2 text-center">
                      <v-progress-circular indeterminate color="primary" size="20" />
                      <span class="ml-2">Processando arquivo...</span>
                    </div>
                    <div v-if="mediaFileSelected && !mediaLoading" class="mt-2">
                      <v-chip close color="primary" outlined small @click:close="removeMediaFile">
                        <v-icon left small>{{ getMediaIcon() }}</v-icon>
                        {{ mediaFileName }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Texto da mensagem -->
                <v-card outlined class="panel-card step-two-subcard mb-3">
                  <v-card-title class="step-two-card-title">
                    <v-icon left small class="mr-1">{{ icons.mdiText }}</v-icon>
                    Texto da Mensagem *
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <validation-provider
                      v-slot="{ errors }"
                      name="Mensagem Parte 1"
                      rules="required"
                      vid="messagePart1"
                    >
                      <v-textarea
                        ref="messagePart1Input"
                        v-model="messagePart1"
                        :error-messages="errors"
                        outlined
                        dense
                        rows="4"
                        auto-grow
                        label="Mensagem Parte 1 *"
                        :placeholder="'Olá {{NOME}}! Aproveite nossa promoção especial...'"
                        class="mb-3"
                        @focus="activeMessageField = 'messagePart1'"
                      />
                    </validation-provider>

                    <validation-provider
                      v-slot="{ errors }"
                      name="Mensagem Parte 2"
                      rules="required"
                      vid="messagePart2"
                    >
                      <v-textarea
                        ref="messagePart2Input"
                        v-model="messagePart2"
                        :error-messages="errors"
                        outlined
                        dense
                        rows="4"
                        auto-grow
                        label="Mensagem Parte 2 *"
                        placeholder="Informações complementares da campanha..."
                        class="mb-3"
                        @focus="activeMessageField = 'messagePart2'"
                      />
                    </validation-provider>

                    <validation-provider
                      v-slot="{ errors }"
                      name="Mensagem Parte 3"
                      rules="required"
                      vid="messagePart3"
                    >
                      <v-textarea
                        ref="messagePart3Input"
                        v-model="messagePart3"
                        :error-messages="errors"
                        outlined
                        dense
                        rows="4"
                        auto-grow
                        label="Mensagem Parte 3 *"
                        placeholder="Detalhes finais ou chamada para ação..."
                        @focus="activeMessageField = 'messagePart3'"
                      />
                    </validation-provider>

                    <div v-if="availableVariables.length" class="message-variables mt-3">
                      <div class="text-caption font-weight-medium mb-2">
                        Variáveis disponíveis — clique para inserir na mensagem:
                      </div>
                      <div class="d-flex flex-wrap" style="gap: 8px;">
                        <v-btn
                          v-for="variable in availableVariables"
                          :key="'msg-var-' + variable"
                          small
                          outlined
                          color="primary"
                          @click="insertVariable(variable)"
                        >
                          {{ variable }}
                        </v-btn>
                      </div>
                      <p class="text-caption mt-2 mb-0">
                        O valor é inserido no formato <code v-pre>{{NOME}}</code> no campo de mensagem em foco.
                      </p>
                    </div>
                    <div v-else class="text-caption mt-3 mb-0">
                      <template v-if="recipientType === 'csv'">
                        Faça upload de um CSV com colunas além de telefone para usar variáveis.
                      </template>
                      <template v-else>
                        Crie variáveis na origem da lista para personalizar a mensagem.
                      </template>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Botão -->
                <v-card outlined class="panel-card step-two-subcard">
                  <v-card-title class="step-two-card-title d-flex flex-wrap align-center">
                    <span class="d-flex align-center">
                      <v-icon left small class="mr-1">{{ icons.mdiLinkVariant }}</v-icon>
                      Botão
                    </span>
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <v-text-field
                      :value="fixedButtonLabel"
                      dense
                      outlined
                      label="Texto do botão"
                      class="mb-2"
                      disabled
                      hide-details
                    />
                    <v-text-field
                      v-model="messageButtons[0].url"
                      dense
                      outlined
                      label="URL"
                      placeholder="https://..."
                      type="url"
                      counter="500"
                      maxlength="500"
                      :rules="buttonUrlRules"
                    />
                  </v-card-text>
                </v-card>
              </div>
            </v-col>

            <v-col cols="12" lg="5">
              <v-card outlined class="panel-card panel-card--full-height step-two-preview-card">
                <v-card-title class="step-two-card-title">
                  <v-icon left small class="mr-1">{{ icons.mdiFileDocumentOutline }}</v-icon>
                  Preview da Mensagem
                </v-card-title>
                <v-card-text class="step-two-preview-card__body pa-2 pa-sm-3">
                  <div class="phone-mockup-wrap step-two-phone-fill d-flex">
                    <div class="phone-mockup phone-mockup--fill">
                      <div class="phone-mockup__inner phone-mockup__inner--fill">
                        <div class="whatsapp-preview whatsapp-preview--phone preview-compact preview-compact--step2">
                          <div class="whatsapp-chat whatsapp-chat--step2-fill">
                            <div class="whatsapp-header whatsapp-header--phone mb-2 flex-shrink-0">
                              <v-avatar v-if="profilePhotoSelected" size="36" class="mr-2">
                                <img :src="profilePhotoPreviewUrl" alt="Perfil" />
                              </v-avatar>
                              <v-avatar v-else size="36" color="success" class="mr-2 white--text">
                                {{ profileInitial }}
                              </v-avatar>
                              <div class="whatsapp-header__text">
                                <div class="profile-name">{{ profileName || 'Empresa' }}</div>
                                <div class="whatsapp-online">online</div>
                              </div>
                            </div>

                            <!-- <div class="preview-chat-spacer" aria-hidden="true" /> -->

                            <div class="whatsapp-message flex-shrink-0">
                              <div class="message-bubble message-bubble--sent">
                                <div v-if="mediaFileSelected" class="media-preview mb-2">
                                  <img v-if="mediaType === 'image'" :src="mediaPreviewUrl" alt="Preview" class="preview-image" />
                                  <video v-else-if="mediaType === 'video'" :src="mediaPreviewUrl" controls class="preview-video" />
                                </div>

                                <template v-if="hasMessageContent">
                                  <p
                                    v-if="messagePart1"
                                    v-html="formatWhatsappMessage(messagePart1)"
                                  />
                                  <p
                                    v-if="messagePart2"
                                    v-html="formatWhatsappMessage(messagePart2)"
                                  />
                                  <p
                                    v-if="messagePart3"
                                    v-html="formatWhatsappMessage(messagePart3)"
                                  />
                                </template>
                                <p v-else-if="!mediaFileSelected" class="text--secondary mb-0">
                                  Digite sua mensagem...
                                </p>

                                <a
                                  v-if="messageButtonUrl"
                                  :href="messageButtonUrl"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="whatsapp-button-link mt-2 d-block"
                                >
                                  {{ fixedButtonLabel }}
                                </a>
                                <span class="message-time">{{ currentTime }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row dense class="mt-4" v-if="currentStep === 1 && !skipsProfileStep">
            <v-col cols="12" class="d-flex justify-end flex-wrap">
              <v-btn color="error" outlined class="me-3 mb-2 mb-sm-0" @click="resetForm">
                Cancelar
              </v-btn>
              <v-btn
                v-if="canSubmitChannelRequest"
                color="info"
                class="next-btn"
                :loading="channelSubmitting"
                @click="submitChannelRequest"
              >
                <v-icon left>{{ icons.mdiSend }}</v-icon>
                Enviar Solicitação
              </v-btn>
            </v-col>
          </v-row>

          <v-row dense class="mt-4" v-if="currentStep === 2">
            <v-col cols="12" class="d-flex justify-space-between flex-wrap">
              <v-btn
                v-if="!skipsProfileStep"
                outlined
                @click="currentStep = 1"
              >
                <v-icon left>{{ icons.mdiArrowLeft }}</v-icon>
                Voltar
              </v-btn>
              <div v-else />
              <div class="d-flex flex-wrap">
                <v-btn color="error" outlined class="me-3 mb-2 mb-sm-0" @click="resetForm">
                  Cancelar
                </v-btn>
                <v-btn
                  color="info"
                  class="next-btn"
                  :disabled="requestingAdditionalChannel"
                  @click="goToMessageStep"
                >
                  <v-icon left>{{ icons.mdiSend }}</v-icon>
                  Próximo: Criar Mensagem
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row dense class="mt-4 step-two-actions" v-if="currentStep === 3">
            <v-col cols="12" class="d-flex flex-wrap justify-space-between align-center">
              <v-btn outlined @click="currentStep = 2">
                <v-icon left>{{ icons.mdiArrowLeft }}</v-icon>
                Voltar
              </v-btn>
              <div class="d-flex flex-wrap justify-end">
                <v-btn color="error" outlined class="me-2 mb-2 mb-sm-0" @click="resetForm">
                  Cancelar
                </v-btn>
                <v-btn color="info" class="next-btn" @click="submitCampaign">
                  <v-icon left>{{ icons.mdiSend }}</v-icon>
                  Enviar Solicitação
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </validation-observer>
      </v-form>
    <!-- </v-card> -->
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { ref } from '@vue/composition-api'
import DatetimePicker from 'vuetify-datetime-picker'
import Vue from 'vue'
import {
  mdiFileUpload,
  mdiSend,
  mdiPaperclip,
  mdiImage,
  mdiVideo,
  mdiMicrophone,
  mdiFile,
  mdiAccount,
  mdiAlert,
  mdiCheckCircle,
  mdiCalendarMonthOutline,
  mdiClockTimeElevenOutline,
  mdiCheck,
  mdiText,
  mdiFileDocumentOutline,
  mdiPlus,
  mdiClose,
  mdiLinkVariant,
  mdiArrowLeft,
  mdiArrowRight,
} from '@mdi/js'

Vue.use(DatetimePicker)

export default {
  setup() {
    const currentStep = ref(1)
    const campaignName = ref('')
    const profileName = ref('')
    const recipientType = ref('csv')
    const csvFile = ref(null)
    const contactsTextarea = ref('')
    const contacts = ref([])
    const messagePart1 = ref('')
    const messagePart2 = ref('')
    const messagePart3 = ref('')
    const scheduledDate = ref(null)
    const anonymizeRecipients = ref(false)

    // Foto de perfil
    const profilePhotoSelected = ref(null)
    const profilePhotoFileName = ref('')
    const profilePhotoPreviewUrl = ref('')
    const profilePhotoLoading = ref(false)

    // Mídia (apenas imagem ou vídeo)
    const mediaFileSelected = ref(null)
    const mediaFileName = ref('')
    const mediaType = ref('')
    const mediaPreviewUrl = ref('')
    const mediaLoading = ref(false)
    const isDragging = ref(false)

    /** Etapa 2: mídia por select + lista de botões (1ª linha espelha API atual) */
    const mediaMode = ref('image')
    const mediaModeItems = [
      { text: 'Imagem', value: 'image' },
      { text: 'Vídeo', value: 'video' },
    ]
    const messageButtons = ref([{ label: 'Clique aqui', url: '' }])
    const fixedButtonLabel = 'Clique aqui'
    const buttonUrlRules = [
      v => !v || String(v).length <= 500 || 'URL do botão deve ter no máximo 500 caracteres',
    ]
    const csvVariableKeys = ref([])
    const customVariables = ref([])
    const newCustomVariable = ref('')
    const activeMessageField = ref('messagePart1')

    const recipientTypes = [
      { text: 'Importar arquivo CSV', value: 'csv' },
      { text: 'Digitar ou Colar', value: 'paste' },
    ]

    // Função para obter hora atual
    const getCurrentTime = () => {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    }

    const currentTime = ref(getCurrentTime())
    const channelRequests = ref([])
    const channelLoading = ref(false)
    const channelSubmitting = ref(false)
    const requestingAdditionalChannel = ref(false)
    const selectedChannelId = ref(null)

    return {
      currentStep,
      campaignName,
      profileName,
      recipientType,
      recipientTypes,
      csvFile,
      contactsTextarea,
      contacts,
      messagePart1,
      messagePart2,
      messagePart3,
      scheduledDate,
      anonymizeRecipients,
      currentTime,
      profilePhotoSelected,
      profilePhotoFileName,
      profilePhotoPreviewUrl,
      profilePhotoLoading,
      mediaFileSelected,
      mediaFileName,
      mediaType,
      mediaPreviewUrl,
      mediaLoading,
      isDragging,
      mediaMode,
      mediaModeItems,
      messageButtons,
      fixedButtonLabel,
      buttonUrlRules,
      csvVariableKeys,
      customVariables,
      newCustomVariable,
      activeMessageField,
      channelRequests,
      channelLoading,
      channelSubmitting,
      requestingAdditionalChannel,
      selectedChannelId,
      icons: {
        mdiFileUpload,
        mdiSend,
        mdiPaperclip,
        mdiImage,
        mdiVideo,
        mdiMicrophone,
        mdiFile,
        mdiAccount,
        mdiAlert,
        mdiCheckCircle,
        mdiCalendarMonthOutline,
        mdiClockTimeElevenOutline,
        mdiCheck,
        mdiText,
        mdiFileDocumentOutline,
        mdiPlus,
        mdiClose,
        mdiLinkVariant,
        mdiArrowLeft,
        mdiArrowRight,
      },
    }
  },

  computed: {
    profileInitial() {
      const n = (this.profileName || '').trim()
      return n ? n.charAt(0).toUpperCase() : 'E'
    },
    messageButtonUrl() {
      const url = this.messageButtons && this.messageButtons[0] && this.messageButtons[0].url
      return url ? String(url).trim() : ''
    },
    messageContent() {
      return [this.messagePart1, this.messagePart2, this.messagePart3]
        .map(part => String(part || '').trim())
        .filter(Boolean)
        .join('\n\n')
    },
    hasMessageContent() {
      return Boolean(this.messageContent)
    },
    availableVariables() {
      if (this.recipientType === 'csv') {
        return this.csvVariableKeys || []
      }
      return this.customVariables || []
    },
    canAddCustomVariable() {
      const normalized = this.normalizeVariableKey(this.newCustomVariable)
      if (!normalized) return false
      return !this.customVariables.includes(normalized)
    },
    pasteContactsLabel() {
      return this.customVariables.length
        ? 'Contatos (NUMERO + variáveis, um por linha)'
        : 'Telefones (um por linha)'
    },
    pasteContactsPlaceholder() {
      if (!this.customVariables.length) {
        return '11999999991\n11999999992\n11999999993'
      }
      const sampleValues = this.customVariables.map((_, idx) => `valor${idx + 1}`).join(',')
      return `5511999999991,${sampleValues}\n5511999999992,${sampleValues}`
    },
    approvedChannels() {
      return (this.channelRequests || []).filter(r => r.status === 3)
    },
    approvedChannel() {
      if (!this.approvedChannels.length) return null

      const targetId = this.selectedChannelId
      if (!targetId) return this.approvedChannels[0] || null

      return this.approvedChannels.find(channel => String(channel.id) === String(targetId))
        || this.approvedChannels[0]
        || null
    },
    approvedChannelOptions() {
      return this.approvedChannels.map(channel => ({
        value: channel.id,
        text: channel.channelPhone
          ? `${channel.profileName} (${channel.channelPhone})`
          : channel.profileName,
      }))
    },
    pendingChannelRequest() {
      return (this.channelRequests || []).find(r => r.status === 1 || r.status === 2) || null
    },
    latestRefusedChannelRequest() {
      return (this.channelRequests || []).find(r => r.status === 4) || null
    },
    hasApprovedChannel() {
      return this.approvedChannels.length > 0
    },
    hasPendingChannelRequest() {
      return !!this.pendingChannelRequest
    },
    canSubmitChannelRequest() {
      return !this.hasPendingChannelRequest
        && (!this.hasApprovedChannel || this.requestingAdditionalChannel)
    },
    canRequestAdditionalChannel() {
      return this.hasApprovedChannel
        && !this.hasPendingChannelRequest
        && !this.requestingAdditionalChannel
    },
    canAccessCampaignSteps() {
      return this.hasApprovedChannel
    },
    skipsProfileStep() {
      return this.hasApprovedChannel
    },
    stepSubtitle() {
      if (this.skipsProfileStep) {
        if (this.currentStep === 2) return 'Etapa 1: Informações do disparo'
        if (this.currentStep === 3) return 'Etapa 2: Criar mensagem'
        return 'Nova solicitação de campanha'
      }

      if (this.currentStep === 1) return 'Etapa 1: Preferências do número'
      if (this.currentStep === 2) return 'Etapa 2: Informações do disparo'
      return 'Etapa 3: Criar mensagem'
    },
    channelStatusLabel() {
      const status = this.pendingChannelRequest?.status
      if (status === 2) return 'em análise'
      return 'pendente'
    },
    approvedChannelProfileImageUrl() {
      const fileId = this.approvedChannel?.idfileProfileImage
      if (!fileId) return ''
      return this.getPublicImageUrl(fileId)
    },
  },

  watch: {
    selectedChannelId() {
      this.applyApprovedChannelToProfile()
    },
  },

  async created() {
    await this.fetchChannelRequests()
  },

  methods: {
    async fetchChannelRequests() {
      this.channelLoading = true
      try {
        const data = await this.findAllChannelRequests()
        this.channelRequests = Array.isArray(data) ? data : []
        this.applyApprovedChannelToProfile()
        this.syncDefaultSelectedChannel()
        this.initializeCurrentStep()
      } catch (error) {
        console.error('Erro ao buscar solicitações de canal:', error)
      } finally {
        this.channelLoading = false
      }
    },

    initializeCurrentStep() {
      if (this.skipsProfileStep) {
        if (this.currentStep === 1) {
          this.currentStep = 2
        }
        return
      }

      this.currentStep = 1
    },

    syncDefaultSelectedChannel() {
      const approvedIds = this.approvedChannels.map(channel => channel.id)
      if (!approvedIds.length) {
        this.selectedChannelId = null
        return
      }

      if (this.selectedChannelId && approvedIds.includes(this.selectedChannelId)) {
        return
      }

      this.selectedChannelId = approvedIds[0]
    },

    startAdditionalChannelRequest() {
      this.requestingAdditionalChannel = true
      this.clearChannelFormFields()
    },

    cancelAdditionalChannelRequest() {
      this.requestingAdditionalChannel = false
      this.clearChannelFormFields()
      this.applyApprovedChannelToProfile()
    },

    applyApprovedChannelToProfile() {
      if (!this.approvedChannel) {
        this.profileName = ''
        this.profilePhotoPreviewUrl = ''
        return
      }

      this.profileName = this.approvedChannel.profileName || ''
      this.profilePhotoPreviewUrl = this.approvedChannel.idfileProfileImage
        ? this.getPublicImageUrl(this.approvedChannel.idfileProfileImage)
        : ''
    },

    getPublicImageUrl(fileId) {
      if (!fileId) return ''
      return `${this.$axios.defaults.baseURL}/public/files/${fileId}`
    },

    async submitChannelRequest() {
      const isValid = await this.validateChannelRequestForm()
      if (!isValid) return

      this.channelSubmitting = true
      try {
        const status = await this.createChannelRequest({
          profileName: this.profileName,
          profilePhoto: this.profilePhotoSelected,
        })

        if (status === 201) {
          this.$notify('Solicitação de canal enviada com sucesso!', 'success')
          this.requestingAdditionalChannel = false
          await this.fetchChannelRequests()
          this.clearChannelFormFields()
          if (this.skipsProfileStep) {
            this.currentStep = 2
          }
        } else {
          const message = status?.response?.data?.message
          this.$notify(message || 'Erro ao enviar solicitação de canal.', 'error')
        }
      } finally {
        this.channelSubmitting = false
      }
    },

    async validateChannelRequestForm(showMessage = true) {
      if (this.profilePhotoLoading) {
        if (showMessage) {
          this.$notify('Aguarde o processamento da foto de perfil para continuar.', 'error')
        }
        return false
      }

      if (!this.profileName || !this.profileName.trim()) {
        if (showMessage) {
          this.$notify('Informe o nome de exibição para continuar.', 'error')
        }
        return false
      }

      if (!this.profilePhotoSelected) {
        if (showMessage) {
          this.$notify('Selecione uma foto de perfil para continuar.', 'error')
        }
        return false
      }

      if (this.currentStep === 1 && this.$refs.formWhatsapp) {
        const isValid = await this.$refs.formWhatsapp.validate()
        if (!isValid) {
          if (showMessage) {
            this.$notify('Preencha os campos obrigatórios para continuar.', 'error')
          }
          return false
        }
      }

      if (this.requestingAdditionalChannel && this.currentStep === 2 && this.$refs.formWhatsapp) {
        const isValid = await this.$refs.formWhatsapp.validate()
        if (!isValid) {
          if (showMessage) {
            this.$notify('Preencha os campos obrigatórios da nova solicitação de canal.', 'error')
          }
          return false
        }
      }

      return true
    },

    clearChannelFormFields() {
      if (this.profilePhotoPreviewUrl && this.profilePhotoSelected) {
        URL.revokeObjectURL(this.profilePhotoPreviewUrl)
      }
      this.profileName = ''
      this.profilePhotoSelected = null
      this.profilePhotoFileName = ''
      this.profilePhotoPreviewUrl = ''
      if (this.$refs.profilePhotoFile) {
        this.$refs.profilePhotoFile.value = ''
      }
    },

    ensureCampaignStepsAccess(showMessage = true) {
      if (this.canAccessCampaignSteps) return true

      if (showMessage) {
        if (this.hasPendingChannelRequest) {
          this.$notify(
            'Aguarde a aprovação do seu canal WhatsApp para continuar com a campanha.',
            'error',
          )
        } else {
          this.$notify(
            'Envie e aguarde a aprovação da solicitação de canal para continuar.',
            'error',
          )
        }
      }

      return false
    },
    async handleStepClick(step) {
      if (step === 1) {
        this.currentStep = 1
        return
      }

      if (step === 2) {
        if (!this.ensureCampaignStepsAccess()) return
        this.currentStep = 2
        return
      }

      if (!this.ensureCampaignStepsAccess()) return
      await this.goToMessageStep()
    },

    async validateProfileStep(showMessage = true) {
      if (!this.ensureCampaignStepsAccess(showMessage)) {
        return false
      }

      if (!this.approvedChannel?.profileName) {
        if (showMessage) {
          this.$notify('Canal aprovado sem nome de exibição configurado.', 'error')
        }
        return false
      }

      this.applyApprovedChannelToProfile()
      return true
    },

    async validateInfoStep(showMessage = true) {
      if (!this.campaignName || !this.campaignName.trim()) {
        if (showMessage) {
          this.$notify('Informe o título da campanha para continuar.', 'error')
        }
        return false
      }

      if (this.approvedChannelOptions.length > 0) {
        if (!this.selectedChannelId) {
          if (showMessage) {
            this.$notify('Selecione uma marca para o disparo.', 'error')
          }
          return false
        }
      }

      if (!this.scheduledDate) {
        if (showMessage) {
          this.$notify('Informe a data e horário da campanha para continuar.', 'error')
        }
        return false
      }

      if (this.currentStep === 2 && this.$refs.formWhatsapp) {
        const isValid = await this.$refs.formWhatsapp.validate()

        if (!isValid) {
          if (showMessage) {
            this.$notify('Preencha os campos obrigatórios da etapa de informações para continuar.', 'error')
          }
          return false
        }
      }

      if (!this.contacts || this.contacts.length === 0) {
        if (showMessage) {
          this.$notify('Adicione pelo menos um contato para continuar.', 'error')
        }
        return false
      }

      const scheduledDateTime = new Date(this.scheduledDate)
      const now = new Date()
      const hoursDifference = (scheduledDateTime - now) / (1000 * 60 * 60)

      if (Number.isNaN(scheduledDateTime.getTime()) || hoursDifference < 3) {
        if (showMessage) {
          this.$notify(
            'A campanha deve ser agendada com no mínimo 3 horas de antecedência.',
            'error',
          )
        }
        return false
      }

      return true
    },

    async validateStepOne(showMessage = true) {
      const isProfileValid = await this.validateProfileStep(showMessage)
      if (!isProfileValid) return false

      return this.validateInfoStep(showMessage)
    },

    async goToInfoStep() {
      if (!this.ensureCampaignStepsAccess()) return
      this.applyApprovedChannelToProfile()
      this.syncDefaultSelectedChannel()
      this.currentStep = 2
    },

    async goToMessageStep() {
      if (this.requestingAdditionalChannel) {
        this.$notify('Finalize ou cancele a solicitação de novo canal antes de continuar.', 'error')
        return
      }

      const isProfileValid = await this.validateProfileStep()
      if (!isProfileValid) return

      const isInfoValid = await this.validateInfoStep()
      if (!isInfoValid) return

      this.currentStep = 3
    },

    onMediaModeChange(val) {
      if (this.mediaFileSelected && this.mediaType && this.mediaType !== val) {
        this.removeMediaFile()
      }
    },

    ...mapActions('serviceWhatsapp', [
      'create',
      'findAllChannelRequests',
      'createChannelRequest',
    ]),

    getMinScheduledDate() {
      const minDate = new Date()
      minDate.setHours(minDate.getHours() + 3)
      const pad = value => String(value).padStart(2, '0')
      return `${minDate.getFullYear()}-${pad(minDate.getMonth() + 1)}-${pad(minDate.getDate())}T${pad(minDate.getHours())}:${pad(minDate.getMinutes())}`
    },

    handleRecipientTypeChange() {
      // Limpa os dados quando muda o tipo
      this.csvFile = null
      this.contactsTextarea = ''
      this.contacts = []
      this.csvVariableKeys = []
      this.customVariables = []
      this.newCustomVariable = ''
    },

    normalizeVariableKey(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_]/g, '')
        .toUpperCase()
    },

    isPhoneHeader(header) {
      return ['telefone', 'phone', 'celular', 'numero', 'numero_whatsapp'].includes(header)
    },

    addCustomVariable() {
      const key = this.normalizeVariableKey(this.newCustomVariable)
      if (!key) {
        this.$notify('Informe um nome válido para a variável.', 'error')
        return
      }
      if (this.isPhoneHeader(key.toLowerCase())) {
        this.$notify('Esse nome é reservado para a coluna de telefone.', 'error')
        return
      }
      if (this.customVariables.includes(key)) {
        this.$notify('Essa variável já foi adicionada.', 'warning')
        return
      }
      this.customVariables = [...this.customVariables, key]
      this.newCustomVariable = ''
      this.processContactsText()
    },

    removeCustomVariable(variable) {
      this.customVariables = this.customVariables.filter(item => item !== variable)
      this.processContactsText()
    },

    getMessageTextareaEl(field) {
      const refName = {
        messagePart1: 'messagePart1Input',
        messagePart2: 'messagePart2Input',
        messagePart3: 'messagePart3Input',
      }[field] || field
      const ref = this.$refs[refName]
      const component = Array.isArray(ref) ? ref[0] : ref
      if (!component) return null
      if (component.$refs && component.$refs.input) return component.$refs.input
      if (typeof component.querySelector === 'function') {
        return component.querySelector('textarea')
      }
      return null
    },

    insertVariable(variable) {
      const key = this.normalizeVariableKey(variable)
      if (!key) return

      const field = ['messagePart1', 'messagePart2', 'messagePart3'].includes(this.activeMessageField)
        ? this.activeMessageField
        : 'messagePart1'
      const token = `{{${key}}}`
      const current = String(this[field] || '')
      const el = this.getMessageTextareaEl(field)

      if (el && typeof el.selectionStart === 'number') {
        const start = el.selectionStart
        const end = el.selectionEnd
        this[field] = current.slice(0, start) + token + current.slice(end)
        this.$nextTick(() => {
          el.focus()
          const pos = start + token.length
          el.setSelectionRange(pos, pos)
        })
        return
      }

      this[field] = current + token
    },

    handleCsvFile(file) {
      if (!file) {
        this.contacts = []
        this.csvVariableKeys = []
        return
      }

      const reader = new FileReader()
      
      reader.onload = e => {
        try {
          const text = String(e.target.result || '')
          const lines = text
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean)
          const parsedContacts = []

          if (lines.length === 0) {
            this.contacts = []
            this.csvVariableKeys = []
            return
          }

          const splitLine = line => (
            line.includes('|')
              ? line.split('|').map(part => part.trim())
              : line.split(',').map(part => part.trim())
          )

          const rawHeaders = splitLine(lines[0])
          const headers = rawHeaders.map(header => (
            header
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase()
          ))

          const phoneColumnIndex = headers.findIndex(header => this.isPhoneHeader(header))

          if (phoneColumnIndex === -1) {
            this.$notify('Arquivo CSV invalido. A coluna "telefone" e obrigatoria.', 'error')
            this.contacts = []
            this.csvVariableKeys = []
            return
          }

          const variableColumns = []
          headers.forEach((header, index) => {
            if (index === phoneColumnIndex || !header) return
            if (this.isPhoneHeader(header)) return
            const key = this.normalizeVariableKey(rawHeaders[index] || header)
            if (!key) return
            if (variableColumns.some(col => col.key === key)) return
            variableColumns.push({ key, index })
          })

          this.csvVariableKeys = variableColumns.map(col => col.key)

          for (let i = 1; i < lines.length; i++) {
            const parts = splitLine(lines[i])
            const phone = String(parts[phoneColumnIndex] || '').replace(/\D/g, '')

            if (!phone) continue

            const contact = { phone }
            variableColumns.forEach(({ key, index }) => {
              const value = String(parts[index] || '').trim()
              if (value) contact[key] = value
            })
            parsedContacts.push(contact)
          }

          this.contacts = parsedContacts
        } catch (error) {
          console.error('Erro ao processar arquivo CSV:', error)
          this.contacts = []
          this.csvVariableKeys = []
        }
      }

      reader.onerror = () => {
        console.error('Erro ao ler arquivo CSV')
        this.contacts = []
        this.csvVariableKeys = []
      }

      reader.readAsText(file)
    },

    processContactsText() {
      if (!this.contactsTextarea) {
        this.contacts = []
        return
      }

      const lines = this.contactsTextarea.split('\n')
      const variables = this.customVariables || []

      this.contacts = lines
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
          const parts = line.includes('|')
            ? line.split('|').map(part => part.trim())
            : line.split(',').map(part => part.trim())

          const phone = String(parts[0] || '').replace(/\D/g, '')
          if (!phone || !/^\d+$/.test(phone)) return null

          // Mesmo formato de SMS/RCS: { NUMERO, ...variáveis }
          if (!variables.length) {
            return { NUMERO: phone }
          }

          const contact = { NUMERO: phone }
          variables.forEach((variable, index) => {
            const value = String(parts[index + 1] || '').trim()
            if (value) contact[variable] = value
          })
          return contact
        })
        .filter(Boolean)
    },

    resetContacts() {
      this.contactsTextarea = ''
      this.contacts = []
    },

    async handleProfilePhoto(event) {
      const file = event.target.files[0]
      if (!file) {
        this.profilePhotoSelected = null
        // Atualizar validação
        if (this.$refs.formWhatsapp) {
          await this.$refs.formWhatsapp.validate()
        }
        return
      }

      this.profilePhotoLoading = true

      try {
        // Validar se é imagem
        if (!file.type.startsWith('image/')) {
          alert('Por favor, selecione apenas arquivos de imagem.')
          event.target.value = ''
          this.profilePhotoLoading = false
          this.profilePhotoSelected = null
          if (this.$refs.formWhatsapp) {
            await this.$refs.formWhatsapp.validate()
          }
          return
        }

        // Validar tamanho (5MB para foto de perfil)
        const maxSize = 5 * 1024 * 1024
        if (file.size > maxSize) {
          alert('A foto de perfil é muito grande. Tamanho máximo: 5MB')
          event.target.value = ''
          this.profilePhotoLoading = false
          this.profilePhotoSelected = null
          if (this.$refs.formWhatsapp) {
            await this.$refs.formWhatsapp.validate()
          }
          return
        }

        this.profilePhotoSelected = file
        this.profilePhotoFileName = file.name

        // Criar URL de preview
        setTimeout(async () => {
          try {
            this.profilePhotoPreviewUrl = URL.createObjectURL(file)
            this.profilePhotoLoading = false
            // Atualizar validação após selecionar arquivo
            if (this.$refs.formWhatsapp) {
              await this.$refs.formWhatsapp.validate()
            }
          } catch (error) {
            console.error('Erro ao criar preview:', error)
            alert('Não foi possível criar o preview da imagem.')
            this.profilePhotoLoading = false
            this.removeProfilePhoto()
          }
        }, 100)

        event.target.value = ''
      } catch (error) {
        console.error('Erro ao processar foto de perfil:', error)
        alert('Erro ao processar imagem. Tente novamente.')
        event.target.value = ''
        this.profilePhotoLoading = false
        this.removeProfilePhoto()
      }
    },

    async removeProfilePhoto() {
      if (this.profilePhotoPreviewUrl) {
        URL.revokeObjectURL(this.profilePhotoPreviewUrl)
      }
      this.profilePhotoSelected = null
      this.profilePhotoFileName = ''
      this.profilePhotoPreviewUrl = ''
      // Atualizar validação
      if (this.$refs.formWhatsapp) {
        await this.$refs.formWhatsapp.validate()
      }
    },

    handleMediaFile(event) {
      const file = event.target.files[0]
      if (!file) return

      this.mediaLoading = true

      try {
        // Validar se é imagem ou vídeo
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
          alert('Por favor, selecione apenas imagens ou vídeos.')
          event.target.value = ''
          this.mediaLoading = false
          return
        }

        // Determinar o tipo de mídia
        let fileType = 'file'

        if (file.type.startsWith('image/')) {
          fileType = 'image'
        } else if (file.type.startsWith('video/')) {
          fileType = 'video'
        }

        // Vídeo: limite de 16MB. Imagem: sem limite de tamanho.
        if (fileType === 'video') {
          const maxVideoSize = 16 * 1024 * 1024
          if (file.size > maxVideoSize) {
            alert('O vídeo é muito grande. Tamanho máximo: 16MB')
            event.target.value = ''
            this.mediaLoading = false
            return
          }
        }

        if (this.mediaMode === 'image' && fileType !== 'image') {
          alert('Selecione um arquivo de imagem.')
          event.target.value = ''
          this.mediaLoading = false
          return
        }
        if (this.mediaMode === 'video' && fileType !== 'video') {
          alert('Selecione um arquivo de vídeo.')
          event.target.value = ''
          this.mediaLoading = false
          return
        }

        this.mediaFileSelected = file
        this.mediaFileName = file.name
        this.mediaType = fileType
        this.mediaMode = fileType

        // Criar URL de preview de forma assíncrona
        setTimeout(() => {
          try {
            this.mediaPreviewUrl = URL.createObjectURL(file)
            this.mediaLoading = false
          } catch (error) {
            console.error('Erro ao criar preview:', error)
            alert('Não foi possível criar o preview do arquivo.')
            this.mediaLoading = false
            this.removeMediaFile()
          }
        }, 100)
        
        // Limpar o input para permitir selecionar o mesmo arquivo novamente
        event.target.value = ''
      } catch (error) {
        console.error('Erro ao processar arquivo de mídia:', error)
        alert('Erro ao processar arquivo. Tente novamente.')
        event.target.value = ''
        this.mediaLoading = false
        this.removeMediaFile()
      }
    },

    removeMediaFile() {
      if (this.mediaPreviewUrl) {
        URL.revokeObjectURL(this.mediaPreviewUrl)
      }
      this.mediaFileSelected = null
      this.mediaFileName = ''
      this.mediaType = ''
      this.mediaPreviewUrl = ''
    },

    getMediaIcon() {
      switch (this.mediaType) {
        case 'image':
          return this.icons.mdiImage
        case 'video':
          return this.icons.mdiVideo
        default:
          return this.icons.mdiFile
      }
    },

    // Funções de drag and drop
    handleDragOver(event) {
      event.preventDefault()
      this.isDragging = true
    },

    handleDragLeave(event) {
      event.preventDefault()
      this.isDragging = false
    },

    handleDrop(event) {
      event.preventDefault()
      this.isDragging = false

      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        
        // Criar um evento fake para passar para handleMediaFile
        const fakeEvent = {
          target: {
            files: [file],
            value: '',
          },
        }
        
        this.handleMediaFile(fakeEvent)
      }
    },

    formatWhatsappMessage(text) {
      // Formata quebras de linha
      return String(text || '').replace(/\n/g, '<br>')
    },

    resetForm() {
      this.requestingAdditionalChannel = false
      this.campaignName = ''
      this.recipientType = 'csv'
      this.csvFile = null
      this.contactsTextarea = ''
      this.contacts = []
      this.messagePart1 = ''
      this.messagePart2 = ''
      this.messagePart3 = ''
      this.messageButtons = [{ label: this.fixedButtonLabel, url: '' }]
      this.mediaMode = 'image'
      this.scheduledDate = null
      this.anonymizeRecipients = false
      this.csvVariableKeys = []
      this.customVariables = []
      this.newCustomVariable = ''
      this.activeMessageField = 'messagePart1'
      this.removeProfilePhoto()
      this.removeMediaFile()
      this.applyApprovedChannelToProfile()
      this.syncDefaultSelectedChannel()
      this.initializeCurrentStep()
    },

    async submitCampaign() {
      const firstBtn = this.messageButtons && this.messageButtons[0]
      console.error('submitCampaign')
      console.error('submitCampaign 2')
      const isStepOneValid = await this.validateStepOne(false)
      if (!isStepOneValid) {
        this.$notify('Preencha corretamente os campos principais da campanha antes de enviar.', 'error')
        return
      }

      // Validar formulário
      console.error('submitCampaign 3')
      
      // Validação do canal aprovado (foto vem do canal no backend)
      if (!this.hasApprovedChannel) {
        this.$notify('É necessário ter um canal WhatsApp aprovado para enviar a campanha.', 'error')
        return
      }

      this.applyApprovedChannelToProfile()
      // Validar quantidade mínima de contatos
      if (!this.contacts || this.contacts.length === 0) {
        this.$notify('Por favor, adicione pelo menos um contato.', 'error')
        return
      }

      const buttonUrl = (firstBtn && firstBtn.url) || ''
      if (buttonUrl && buttonUrl.length > 500) {
        this.$notify('URL do botão deve ter no máximo 500 caracteres. Encurte o link ou remova parâmetros UTM extras.', 'error')
        return
      }
      
      if (!this.$refs.formWhatsapp) {
        console.error('formWhatsapp ref não encontrado!')
        return
      }
      
      const isValid = await this.$refs.formWhatsapp.validate()
      console.error('Validação do formulário:', isValid)
      
      if (!isValid) {
        this.$notify('Por favor, preencha todos os campos obrigatórios corretamente.', 'error')
        return
      }

        const body = {
          campaignName: this.campaignName,
          profileName: this.approvedChannel?.profileName || this.profileName,
          profilePhoto: this.profilePhotoSelected || null,
          recipientType: this.recipientType,
          contacts: this.contacts,
          messageContent: this.messageContent,
          mediaFile: this.mediaFileSelected,
          buttonLink: (firstBtn && firstBtn.url) || '',
          buttonName: (firstBtn && firstBtn.url) ? this.fixedButtonLabel : '',
          scheduleType: 'schedule',
          scheduledDate: this.$day(this.scheduledDate).format('YYYY-MM-DD HH:mm:ss'),
          anonymizeRecipients: this.anonymizeRecipients,
          channelRequestIds: this.selectedChannelId ? [this.selectedChannelId] : [],
        }

      console.error('submitCampaign 4 - Body antes de enviar:', {
        ...body,
        profilePhoto: body.profilePhoto ? { name: body.profilePhoto.name, size: body.profilePhoto.size, type: body.profilePhoto.type } : null,
        contactsLength: body.contacts ? body.contacts.length : 0
      })
        const status = await this.create(body)

        if (status === 201) {
          this.$router.push('/report/service')
          this.resetForm()
      }
    },
  },
}
</script>

<style scoped>
.whatsapp-page {
  padding: 4px;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
}

.page-subtitle {
  font-size: 14px;
  opacity: 0.8;
}

.main-shell {
  padding: 18px;
  border-radius: 14px;
}

.approved-channels-list {
  border: 1px solid rgba(120, 130, 170, 0.2);
  border-radius: 8px;
  padding: 4px 12px;
}

.steps-bar {
  display: flex;
  align-items: center;
  background: rgba(120, 130, 170, 0.12);
  border-radius: 12px;
  padding: 12px 18px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  opacity: 0.75;
}

.step-item--clickable {
  cursor: pointer;
}

.step-item--active {
  opacity: 1;
  font-weight: 600;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: rgba(120, 130, 170, 0.25);
}

.step-item--active .step-number {
  background: #5e6ad2;
  color: #fff;
}

.step-divider {
  flex: 1;
  height: 1px;
  margin: 0 14px;
  background: rgba(120, 130, 170, 0.35);
}

.steps-bar--progress .step-divider--done {
  background: rgba(76, 175, 80, 0.65);
}

.step-divider--done {
  background: rgba(76, 175, 80, 0.65);
}

.step-number--check {
  background: #4caf50 !important;
  width: 26px;
  height: 26px;
  padding: 0;
}

.panel-card {
  border-radius: 12px;
}

.panel-card--full-height {
  height: 100%;
}

.step-item--disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.step-profile-row,
.step-info-row {
  align-items: stretch;
}

.step-one-row {
  align-items: stretch;
}

.panel-title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
}

.upload-card {
  border-radius: 10px;
}

.variable-create-field {
  flex: 1 1 200px;
  min-width: 180px;
  max-width: 320px;
}

.message-variables .v-btn {
  text-transform: none;
  letter-spacing: 0.02em;
}

.next-btn {
  min-width: 240px;
}

.step-two-row {
  align-items: stretch;
}

.step-two-stack {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.step-two-subcard .v-card__title {
  padding-bottom: 8px;
}

.step-two-card-title {
  font-size: 15px;
  font-weight: 600;
  justify-content: flex-start !important;
  text-align: left;
  flex-wrap: wrap;
}

.step-two-preview-card {
  display: flex;
  flex-direction: column;
  min-height: 560px;
  height: 100%;
}

.step-two-preview-card .v-card__text.step-two-preview-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.step-two-phone-fill {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  align-items: stretch;
  justify-content: stretch;
}

.step-two-preview-card .phone-mockup--fill {
  max-width: 100%;
  width: 100%;
  flex: 1 1 auto;
  min-height: 420px;
  display: flex;
  flex-direction: column;
}

.step-two-preview-card .phone-mockup__inner--fill {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.preview-compact--step2 {
  flex: 1 1 auto;
  min-height: 0 !important;
  display: flex;
  flex-direction: column;
}

.whatsapp-chat--step2-fill {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.preview-chat-spacer {
  flex: 1 1 auto;
  min-height: 24px;
  width: 100%;
}

.step-two-preview-card .phone-input-mock--sticky {
  margin-top: auto;
}

.variable-code {
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(120, 130, 170, 0.15);
}

.variable-hint code {
  font-size: 11px;
  margin-right: 4px;
}

.phone-mockup-wrap {
  width: 100%;
  padding: 8px 0 4px;
}

.step-two-preview-card .phone-mockup-wrap {
  padding: 0;
}

.phone-mockup {
  width: 100%;
  max-width: 320px;
  border-radius: 28px;
  padding: 10px;
  background: linear-gradient(145deg, #2a2f3d, #1a1e28);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.phone-mockup__inner {
  border-radius: 12px;
  overflow: hidden;
  background: #ece5dd;
}

.whatsapp-preview--phone {
  min-height: 0;
  padding: 12px;
}

.step-two-preview-card .whatsapp-preview--phone.preview-compact--step2 {
  padding: 14px 12px 12px;
}

.step-two-preview-card .whatsapp-preview {
  min-height: 0;
}

.whatsapp-header--phone {
  padding: 6px 8px;
  margin-bottom: 8px;
}

.whatsapp-header__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.whatsapp-online {
  font-size: 11px;
  color: #128c7e;
  line-height: 1.2;
}

.whatsapp-preview--phone .whatsapp-chat {
  width: 100%;
}

.whatsapp-preview--phone .whatsapp-message {
  align-self: flex-start;
  max-width: 92%;
}

.message-bubble--sent {
  background-color: #ffffff !important;
  border-radius: 8px 0 8px 8px;
}

.phone-input-mock {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.phone-input-mock__placeholder {
  font-size: 13px;
  color: #999;
}

@media (max-width: 1263px) {
  .step-two-actions .v-btn {
    margin-bottom: 8px;
  }
}

/* Etapa 2 — preview ocupa o card */
@media (max-width: 1263px) {
  .step-two-preview-card {
    min-height: 480px;
  }
}

.whatsapp-preview {
  background: linear-gradient(to bottom, #dfe5e7 0%, #dfe5e7 100%);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 800 800'%3E%3Cg fill='none' stroke='%23ccc' stroke-width='1'%3E%3Cpath d='M769 229L1037 260.9M927 880L731 737 520 660 309 538 40 599 295 764 126.5 879.5 40 599-197 493 102 382-31 229 126.5 79.5-69-63'/%3E%3Cpath d='M-31 229L237 261 390 382 603 493 308.5 537.5 101.5 381.5M370 905L295 764'/%3E%3Cpath d='M520 660L578 842 731 737 840 599 603 493 520 660 295 764 309 538 390 382 539 269 769 229 577.5 41.5 370 105 295 -36 126.5 79.5 237 261 102 382 40 599 -69 737 127 880'/%3E%3Cpath d='M520-140L578.5 42.5 731-63M603 493L539 269 237 261 370 105M902 382L539 269M390 382L102 382'/%3E%3Cpath d='M-222 42L126.5 79.5 370 105 539 269 577.5 41.5 927 80 769 229 902 382 603 493 731 737M295-36L577.5 41.5M578 842L295 764M40-201L127 80M102 382L-261 269'/%3E%3C/g%3E%3Cg fill='%23d4d4d4'%3E%3Ccircle cx='769' cy='229' r='5'/%3E%3Ccircle cx='539' cy='269' r='5'/%3E%3Ccircle cx='603' cy='493' r='5'/%3E%3Ccircle cx='731' cy='737' r='5'/%3E%3Ccircle cx='520' cy='660' r='5'/%3E%3Ccircle cx='309' cy='538' r='5'/%3E%3Ccircle cx='295' cy='764' r='5'/%3E%3Ccircle cx='40' cy='599' r='5'/%3E%3Ccircle cx='102' cy='382' r='5'/%3E%3Ccircle cx='127' cy='80' r='5'/%3E%3Ccircle cx='370' cy='105' r='5'/%3E%3Ccircle cx='578' cy='42' r='5'/%3E%3Ccircle cx='237' cy='261' r='5'/%3E%3Ccircle cx='390' cy='382' r='5'/%3E%3C/g%3E%3C/svg%3E");
  padding: 20px;
  border-radius: 8px;
  min-height: 300px;
}

.whatsapp-chat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.whatsapp-message {
  max-width: 80%;
  margin-bottom: 10px;
}

.message-bubble {
  background-color: #fff;
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  word-wrap: break-word;
}

.message-bubble p {
  margin: 0;
  color: #303030;
  font-size: 14px;
  line-height: 1.4;
}

.message-time {
  display: block;
  text-align: right;
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

/* Estilos para preview de mídia */
.media-preview {
  margin-bottom: 8px;
}

.preview-image {
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 8px;
  display: block;
}

.preview-video {
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 8px;
  display: block;
}

.preview-audio {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #e8f5e9;
  padding: 8px;
  border-radius: 8px;
}

.audio-player {
  width: 100%;
  max-width: 250px;
  height: 32px;
}

/* Botão com borda tracejada */
.dashed-border {
  border-style: dashed !important;
  border-width: 2px !important;
}

.media-upload-btn {
  min-height: 60px !important;
  height: 60px !important;
}

/* Drop zone */
.drop-zone {
  transition: all 0.3s ease;
}

.drop-zone-active {
  transform: scale(1.02);
  opacity: 0.8;
}

.drop-zone-active .media-upload-btn {
  background-color: rgba(25, 118, 210, 0.1) !important;
  border-color: #1976d2 !important;
  border-width: 3px !important;
}

/* Estilos para cabeçalho do WhatsApp */
.whatsapp-header {
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  margin-bottom: 10px;
}

.profile-name {
  font-weight: 500;
  font-size: 14px;
  color: #303030;
}

/* Estilo do botão no preview do WhatsApp */
.whatsapp-button-link {
  display: block;
  background-color: #e6e6e6;
  color: #000000 !important;
  text-align: center;
  padding: 10px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.whatsapp-button-link:hover {
  background-color: #f5f5f5;
  text-decoration: none;
  color: #000000 !important;
}
</style>

